import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const reasonsEn = [
  { icon: 'ri-medal-line', title: 'Premium Materials Only', desc: 'We use only tempered safety glass, brushed stainless hardware, and top-grade seals — no shortcuts.' },
  { icon: 'ri-ruler-2-line', title: '100% Custom Work', desc: 'Every installation is measured and fabricated to your exact specifications. No off-the-shelf products.' },
  { icon: 'ri-time-line', title: 'Fast Turnaround', desc: 'Most projects are completed within 5–7 business days from measurement to final installation.' },
  { icon: 'ri-map-pin-line', title: 'Local Las Vegas Experts', desc: 'Family-owned and operated in Las Vegas for 15+ years. We know the local market and building codes.' },
  { icon: 'ri-customer-service-2-line', title: 'Lifetime Warranty', desc: 'We stand behind our work with a lifetime warranty on all hardware and a 5-year warranty on glass.' },
  { icon: 'ri-shield-check-line', title: 'Insured & Bonded', desc: 'Fully insured and bonded for every job — protecting your home and giving you complete peace of mind.' },
  { icon: 'ri-tools-line', title: 'Handyman Services', desc: 'Beyond glass — we handle minor repairs, adjustments, and handyman tasks so you only need one call.' },
];

const reasonsEs = [
  { icon: 'ri-medal-line', title: 'Solo Materiales Premium', desc: 'Usamos solo vidrio de seguridad templado, hardware de acero inoxidable y sellos de primera calidad.' },
  { icon: 'ri-ruler-2-line', title: 'Trabajo 100% Personalizado', desc: 'Cada instalación se mide y fabrica según sus especificaciones exactas. Sin productos genéricos.' },
  { icon: 'ri-time-line', title: 'Entrega Rápida', desc: 'La mayoría de los proyectos se completan en 5–7 días hábiles desde la medición hasta la instalación.' },
  { icon: 'ri-map-pin-line', title: 'Expertos Locales de Las Vegas', desc: 'Empresa familiar en Las Vegas por más de 15 años. Conocemos el mercado local y los códigos de construcción.' },
  { icon: 'ri-customer-service-2-line', title: 'Garantía de Por Vida', desc: 'Respaldamos nuestro trabajo con garantía de por vida en hardware y 5 años en vidrio.' },
  { icon: 'ri-shield-check-line', title: 'Asegurado y Afianzado', desc: 'Completamente asegurado y afianzado en cada trabajo — protegiendo su hogar y dándole total tranquilidad.' },
  { icon: 'ri-tools-line', title: 'Servicios de Mantenimiento', desc: 'Más allá del vidrio — manejamos reparaciones menores y tareas de mantenimiento para que solo necesite una llamada.' },
];

const stats = [
  { num: 15, suffix: '+', label: 'Years Experience', labelEs: 'Años de Experiencia' },
  { num: 2000, suffix: '+', label: 'Projects Done', labelEs: 'Proyectos Completados' },
  { num: 500, suffix: '+', label: '5-Star Reviews', labelEs: 'Reseñas 5 Estrellas' },
  { num: 100, suffix: '%', label: 'Satisfaction', labelEs: 'Satisfacción' },
];

function useCountUp(target: number, active: boolean, duration = 1500) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}

const StatCard = ({ stat, active, isEs }: { stat: typeof stats[0]; active: boolean; isEs: boolean }) => {
  const count = useCountUp(stat.num, active);
  return (
    <div className="text-center">
      <p className="text-4xl md:text-5xl font-bold text-black">
        {count.toLocaleString()}{stat.suffix}
      </p>
      <p className="text-gray-500 text-sm mt-1">{isEs ? stat.labelEs : stat.label}</p>
    </div>
  );
};

const WhyChooseUs = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const [itemsVisible, setItemsVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const { i18n } = useTranslation();
  const isEs = i18n.resolvedLanguage === 'es';
  const reasons = isEs ? reasonsEs : reasonsEn;

  useEffect(() => {
    const obs1 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setItemsVisible(true); }, { threshold: 0.1 });
    const obs2 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsVisible(true); }, { threshold: 0.3 });
    if (sectionRef.current) obs1.observe(sectionRef.current);
    if (statsRef.current) obs2.observe(statsRef.current);
    return () => { obs1.disconnect(); obs2.disconnect(); };
  }, []);

  return (
    <section className="py-20 md:py-28 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Stats Row */}
        <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20 pb-16 border-b border-gray-100">
          {stats.map((stat) => (
            <StatCard key={stat.label} stat={stat} active={statsVisible} isEs={isEs} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">
              {isEs ? 'Por Qué Quinolux' : 'Why Quinolux'}
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-black leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {isEs ? 'Artesanía de Precisión,' : 'Precision Craftsmanship,'}<br />
              <span className="font-semibold">{isEs ? 'Siempre' : 'Every Time'}</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-10 max-w-md">
              {isEs
                ? 'No somos solo instaladores de vidrio — somos artesanos que se enorgullecen de cada corte, cada sello y cada instalación terminada.'
                : 'We\'re not just glass installers — we\'re craftsmen who take pride in every cut, every seal, and every finished installation.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {reasons.map((r, i) => (
                <div
                  key={r.title}
                  className={`flex gap-4 transition-all duration-600 ease-out ${
                    itemsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-black text-white flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${r.icon} text-base`}></i>
                  </div>
                  <div>
                    <h3 className="text-black font-semibold text-sm mb-1">{r.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Image Collage */}
          <div className="relative h-[520px] hidden lg:block">
            <div className="absolute top-0 left-0 w-[58%] h-[62%] rounded-2xl overflow-hidden group">
              <img src="https://public.readdy.ai/ai/img_res/1ebf715c30b073282e016347fc5133d8.jpg" alt="Professional glass installation Las Vegas" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
            </div>
            <div className="absolute bottom-0 left-0 w-[45%] h-[42%] rounded-2xl overflow-hidden group">
              <img src="https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20frameless%20glass%20shower%20door%20hardware%20brushed%20nickel%20hinges%20premium%20quality%20installation%2C%20macro%20photography%2C%20clean%20background&width=400&height=300&seq=why002&orientation=landscape" alt="Premium glass hardware detail" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
            </div>
            <div className="absolute top-8 right-0 w-[40%] h-[55%] rounded-2xl overflow-hidden group">
              <img src="https://readdy.ai/api/search-image?query=modern%20glass%20stair%20railing%20in%20luxury%20home%20with%20clear%20tempered%20glass%20panels%2C%20contemporary%20interior%20design%2C%20natural%20light%2C%20open%20concept%20living%20space&width=400&height=500&seq=why003&orientation=portrait" alt="Glass railing installation Las Vegas" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
            </div>
            <div className="absolute bottom-0 right-0 w-[38%] h-[38%] rounded-2xl overflow-hidden group">
              <img src="https://readdy.ai/api/search-image?query=large%20custom%20bathroom%20mirror%20with%20LED%20backlight%20in%20modern%20bathroom%2C%20frameless%20design%2C%20white%20walls%2C%20luxury%20interior%20photography&width=400&height=300&seq=why004&orientation=landscape" alt="Custom mirror installation" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl p-5 z-10 text-center w-32 border border-gray-100">
              <p className="text-3xl font-bold text-black">15+</p>
              <p className="text-gray-500 text-xs mt-1">{isEs ? 'Años en Las Vegas' : 'Years in Las Vegas'}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
